'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { marketSuppliersColumnsDefs, PAGE_SIZE } from './config';
import { marketSuppliersTableFeatures } from './config/MarketSuppliers.columns';
import { useMarketSuppliersData, useMarketSuppliersRow } from './hooks';
export interface MarketSuppliersClientProps {
  chainId: ChainId;
  marketId: string;
}
const MarketSuppliersClient = t0 => {
  const $ = _c(46);
  const {
    chainId,
    marketId
  } = t0;
  let t1;
  if ($[0] !== chainId || $[1] !== marketId) {
    t1 = {
      chainId,
      marketId,
      first: PAGE_SIZE
    };
    $[0] = chainId;
    $[1] = marketId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    page,
    items,
    partial,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useMarketSuppliersData(t1);
  let t2;
  if ($[3] !== chainId || $[4] !== items || $[5] !== pageIndex) {
    t2 = {
      items,
      chainId,
      currentPage: pageIndex
    };
    $[3] = chainId;
    $[4] = items;
    $[5] = pageIndex;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const formattedData = useMarketSuppliersRow(t2);
  let t3;
  if ($[7] !== formattedData) {
    t3 = {
      features: marketSuppliersTableFeatures,
      data: formattedData,
      columns: marketSuppliersColumnsDefs,
      enableSorting: false
    };
    $[7] = formattedData;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const marketSuppliersTable = useTable(t3);
  const loading = isLoading || isLoadingNextPage;
  let t4;
  if ($[9] !== marketSuppliersTable) {
    t4 = marketSuppliersTable.getRowModel();
    $[9] = marketSuppliersTable;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const emptyRows = t4.rows.length === 0;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs: marketSuppliersColumnsDefs
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const skeletonRows = useSkeletonRows(t5);
  let t6;
  if ($[12] !== emptyRows || $[13] !== loading || $[14] !== marketSuppliersTable || $[15] !== skeletonRows) {
    t6 = (!emptyRows || loading) && <Table.Table tableVariant="compact"><Table.Thead>{marketSuppliersTable.getHeaderGroups().map(_temp)}</Table.Thead><Table.Tbody>{!loading && marketSuppliersTable.getRowModel().rows.map(_temp2)}{loading && skeletonRows.map(_temp3)}</Table.Tbody></Table.Table>;
    $[12] = emptyRows;
    $[13] = loading;
    $[14] = marketSuppliersTable;
    $[15] = skeletonRows;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== emptyRows || $[18] !== error || $[19] !== loading) {
    t7 = emptyRows && !loading && !error && <TableNoResults text="No suppliers found." height="478px" style={{
      borderRadius: "0"
    }} />;
    $[17] = emptyRows;
    $[18] = error;
    $[19] = loading;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== error || $[22] !== loading) {
    t8 = !loading && error && <ErrorDataUnavailable minHeight="478px" style={{
      borderRadius: "0"
    }} />;
    $[21] = error;
    $[22] = loading;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t6 || $[25] !== t7 || $[26] !== t8) {
    t9 = <TableScrollArea className="grid h-full w-full rounded-[20px] [&_td:first-of-type]:w-1/2 [&_td:nth-of-type(2)]:w-auto">{t6}{t7}{t8}</TableScrollArea>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== error || $[29] !== loading || $[30] !== partial) {
    t10 = partial && !loading && !error && <p className="text-secondary-foreground px-sm text-xs" data-testid="market-suppliers-partial-notice">Some suppliers could not be fully loaded, so this page may be incomplete.</p>;
    $[28] = error;
    $[29] = loading;
    $[30] = partial;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const t11 = page ? Math.ceil(page.countTotal / PAGE_SIZE) : undefined;
  let t12;
  if ($[32] !== canNextPage || $[33] !== canPreviousPage || $[34] !== error || $[35] !== loading || $[36] !== nextPage || $[37] !== pageIndex || $[38] !== prefetchNext || $[39] !== previousPage || $[40] !== t11) {
    t12 = <CursorPagination loading={loading} error={error} pageIndex={pageIndex} pageCount={t11} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[32] = canNextPage;
    $[33] = canPreviousPage;
    $[34] = error;
    $[35] = loading;
    $[36] = nextPage;
    $[37] = pageIndex;
    $[38] = prefetchNext;
    $[39] = previousPage;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t10 || $[43] !== t12 || $[44] !== t9) {
    t13 = <div className="gap-lg flex h-full w-full flex-col"><div className="gap-xs flex flex-col">{t9}{t10}{t12}</div></div>;
    $[42] = t10;
    $[43] = t12;
    $[44] = t9;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
};
export default MarketSuppliersClient;
function _temp(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp2(row) {
  return <TrBodyRow key={row.id} row={row} />;
}
function _temp3(row_0, index) {
  return <TrBodyRow key={index + "skeleton-table-market-suppliers"} skeletonRow={row_0} />;
}